import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";

const CorporateGovernance = () => {
  const [activeSection, setActiveSection] = useState(null);

  // Governance principles data
  const governancePrinciples = [
    {
      id: 1,
      title: "Defined Approval & Verification",
      description: "Structured approval and verification processes for all applications ensuring thorough due diligence.",
      icon: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      )
    },
    {
      id: 2,
      title: "Accurate Record Maintenance",
      description: "Maintenance of accurate records and documentation with complete transparency.",
      icon: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
        </svg>
      )
    },
    {
      id: 3,
      title: "Regular Process Monitoring",
      description: "Regular monitoring of operations to ensure process integrity and compliance standards.",
      icon: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
        </svg>
      )
    },
    {
      id: 4,
      title: "Ethical Conduct",
      description: "Commitment to ethical conduct and fair dealings in all business operations.",
      icon: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      )
    }
  ];

  // Compliance badges data
  const complianceBadges = [
    { label: "Companies Act, 2013", year: "Compliant" },
    { label: "Data Protection", year: "GDPR Ready" },
    { label: "ISO 27001", year: "Certified" },
    { label: "Financial Conduct", year: "Regulated" }
  ];

  return (
    <section className="relative w-full min-h-screen bg-[#0A1620] overflow-hidden">
      {/* PREMIUM DARK GRADIENT BACKGROUND */}
      <div className="absolute inset-0">
        {/* Base Dark Color */}
        <div className="absolute inset-0 bg-[#0A1620]"></div>

        {/* Deep Blue-Green Accent - Top Right */}
        <div className="absolute -top-40 -right-40 w-[600px] h-[600px] bg-[#1B4D5E]/20 rounded-full blur-[120px]"></div>

        {/* Navy Accent - Bottom Left */}
        <div className="absolute -bottom-40 -left-40 w-[500px] h-[500px] bg-[#1A3B4D]/20 rounded-full blur-[100px]"></div>

        {/* Very Subtle Grid Pattern */}
        <div className="absolute inset-0 opacity-[0.03]">
          <div
            className="absolute inset-0"
            style={{
              backgroundImage: `linear-gradient(to right, #2D5F7A 1px, transparent 1px), linear-gradient(to bottom, #2D5F7A 1px, transparent 1px)`,
              backgroundSize: "60px 60px",
            }}
          ></div>
        </div>

        {/* TOP WAVE - Upper Side */}
        <svg
          className="absolute top-0 left-0 w-full h-48 rotate-180"
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 1440 320"
          preserveAspectRatio="none"
        >
          <path
            fill="#2D5F7A"
            fillOpacity="0.08"
            d="M0,192L48,176C96,160,192,128,288,128C384,128,480,160,576,176C672,192,768,192,864,176C960,160,1056,128,1152,128C1248,128,1344,160,1392,176L1440,192L1440,320L1392,320C1344,320,1248,320,1152,320C1056,320,960,320,864,320C768,320,672,320,576,320C480,320,384,320,288,320C192,320,96,320,48,320L0,320Z"
          ></path>
        </svg>

        {/* BOTTOM WAVE - Lower Side */}
        <svg
          className="absolute bottom-0 right-0 w-full h-48"
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 1440 320"
          preserveAspectRatio="none"
        >
          <path
            fill="#2D5F7A"
            fillOpacity="0.08"
            d="M0,192L48,176C96,160,192,128,288,128C384,128,480,160,576,176C672,192,768,192,864,176C960,160,1056,128,1152,128C1248,128,1344,160,1392,176L1440,192L1440,320L1392,320C1344,320,1248,320,1152,320C1056,320,960,320,864,320C768,320,672,320,576,320C480,320,384,320,288,320C192,320,96,320,48,320L0,320Z"
          ></path>
        </svg>
      </div>

      {/* MAIN CONTENT */}
      <div className="relative z-20 h-full">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 w-full py-24 lg:py-28">
          {/* Header Section */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            {/* Premium Badge */}
            <div className="inline-flex items-center gap-2 bg-[#1E3B4A]/30 px-4 py-2 rounded-full border border-[#2D5F7A]/30 backdrop-blur-sm mb-6">
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#4D9ABF] opacity-40"></span>
                <span className="relative inline-flex rounded-full h-2 w-2 bg-[#4D9ABF]"></span>
              </span>
              <span className="text-[#B2D9F0] text-xs font-bold uppercase tracking-wider">
                Transparency & Accountability
              </span>
            </div>

            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-4">
              Corporate{" "}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#7BC4E0] to-[#4D9ABF]">
                Governance
              </span>
            </h1>

            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.3 }}
              className="text-xl text-[#CCE6F5] font-light max-w-3xl mx-auto leading-relaxed"
            >
              At SalaryMates, we follow a structured governance framework
              designed to ensure transparency, accountability, and ethical
              financial practices across all operations.
            </motion.p>
          </motion.div>

          {/* Mission Statement Card */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.4 }}
            className="bg-gradient-to-br from-[#1A2F3A] to-[#0F2128] rounded-[24px] p-8 md:p-10 mb-16 border border-[#2D5F7A]/30 shadow-2xl"
          >
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-full bg-[#1E3B4A] flex items-center justify-center flex-shrink-0 border border-[#7BC4E0]/30">
                <svg
                  className="w-6 h-6 text-[#7BC4E0]"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M13 10V3L4 14h7v7l9-11h-7z"
                  />
                </svg>
              </div>
              <div>
                <h2 className="text-2xl font-semibold text-white mb-3">
                  Our Objective
                </h2>
                <p className="text-[#CCE6F5] text-lg leading-relaxed">
                  As a socially driven organization, our objective is to provide
                  accessible financial assistance solutions while maintaining
                  strong internal controls and responsible decision-making.
                </p>
              </div>
            </div>
          </motion.div>

          {/* Governance Principles Grid */}
          <div className="grid md:grid-cols-2 gap-6 mb-16">
            {governancePrinciples.map((principle, index) => (
              <motion.div
                key={principle.id}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5 + index * 0.1 }}
                onHoverStart={() => setActiveSection(principle.id)}
                onHoverEnd={() => setActiveSection(null)}
                className={`group relative bg-gradient-to-br from-[#1A2F3A] to-[#0F2128] rounded-[20px] p-6 border transition-all duration-500 ${
                  activeSection === principle.id
                    ? "border-[#7BC4E0] shadow-lg shadow-[#7BC4E0]/10"
                    : "border-[#2D5F7A]/30 hover:border-[#2D5F7A]"
                }`}
              >
                {/* Hover Effect Background */}
                <div
                  className={`absolute inset-0 bg-gradient-to-r from-[#2D5F7A]/10 to-transparent rounded-[20px] transition-opacity duration-500 ${
                    activeSection === principle.id ? "opacity-100" : "opacity-0"
                  }`}
                ></div>

                <div className="relative z-10">
                  <div
                    className={`w-12 h-12 rounded-full bg-[#1E3B4A] flex items-center justify-center mb-4 border transition-all duration-500 ${
                      activeSection === principle.id
                        ? "border-[#7BC4E0]"
                        : "border-[#2D5F7A]/30"
                    }`}
                  >
                    <div
                      className={`transition-colors duration-500 ${
                        activeSection === principle.id
                          ? "text-[#7BC4E0]"
                          : "text-[#CCE6F5]"
                      }`}
                    >
                      {principle.icon}
                    </div>
                  </div>
                  <h3
                    className={`text-xl font-semibold mb-2 transition-colors duration-500 ${
                      activeSection === principle.id
                        ? "text-white"
                        : "text-[#CCE6F5]"
                    }`}
                  >
                    {principle.title}
                  </h3>
                  <p className="text-[#8FAFC7] leading-relaxed">
                    {principle.description}
                  </p>
                </div>

                {/* Decorative Corner */}
                <div
                  className={`absolute top-0 right-0 w-12 h-12 border-t border-r rounded-tr-[20px] transition-opacity duration-500 ${
                    activeSection === principle.id
                      ? "border-[#7BC4E0] opacity-100"
                      : "border-transparent opacity-0"
                  }`}
                ></div>
              </motion.div>
            ))}
          </div>

          {/* Compliance Statement */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.9 }}
            className="bg-gradient-to-br from-[#1A2F3A] to-[#0F2128] rounded-[24px] p-8 md:p-10 border border-[#2D5F7A]/30 mb-12"
          >
            <div className="grid lg:grid-cols-2 gap-10 items-center">
              {/* Left Side - Text */}
              <div>
                <div className="inline-flex items-center gap-2 bg-[#1E3B4A]/30 px-3 py-1 rounded-full border border-[#2D5F7A]/30 mb-4">
                  <span className="w-1.5 h-1.5 bg-[#4D9ABF] rounded-full"></span>
                  <span className="text-[#B2D9F0] text-xs font-medium">
                    Regulatory Compliance
                  </span>
                </div>
                <h3 className="text-2xl font-semibold text-white mb-4">
                  Alignment with Companies Act, 2013
                </h3>
                <p className="text-[#CCE6F5] text-lg leading-relaxed">
                  We strive to operate in alignment with applicable provisions
                  of the Companies Act, 2013, ensuring that all activities are
                  carried out with discipline, fairness, and responsibility.
                </p>

                {/* Compliance Badges */}
                <div className="flex flex-wrap gap-3 mt-6">
                  {complianceBadges.map((badge, index) => (
                    <div
                      key={index}
                      className="flex items-center gap-2 bg-[#1E3B4A]/20 backdrop-blur-sm px-4 py-2 rounded-lg border border-[#2D5F7A]/20"
                    >
                      <span className="text-[#7BC4E0] text-sm font-medium">
                        {badge.label}
                      </span>
                      <span className="text-[#8FAFC7] text-xs">•</span>
                      <span className="text-[#B2D9F0] text-xs">
                        {badge.year}
                      </span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Right Side - Visual Stats */}
              <div className="w-full max-w-lg h-80 rounded-2xl overflow-hidden border-2 border-[#7BC4E0]/20 shadow-2xl">
                <img
                  src="/images/cg1.webp"
                  alt="Corporate Governance"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
          </motion.div>

          {/* Footer CTA */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1 }}
            className="flex justify-center gap-4 mt-8"
          >
            <Link
              to="/contact"
              className="group px-8 py-4 bg-gradient-to-r from-[#2D5F7A] to-[#1E3B4A] text-white font-semibold text-base rounded-lg shadow-lg hover:shadow-[#2D5F7A]/30 transition-all duration-300 hover:scale-105 flex items-center gap-2 border border-[#7BC4E0]/20"
            >
              Learn More About Our Governance
              <svg
                className="w-4 h-4 group-hover:translate-x-1 transition-transform"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M13 7l5 5m0 0l-5 5m5-5H6"
                />
              </svg>
            </Link>
          </motion.div>
        </div>
      </div>

      {/* Decorative Elements */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex gap-2 z-30">
        <span className="text-[#2D5F7A] text-sm">Governance</span>
        <span className="text-[#2D5F7A]">•</span>
        <span className="text-[#7BC4E0] text-sm">Compliance</span>
        <span className="text-[#2D5F7A]">•</span>
        <span className="text-[#2D5F7A] text-sm">Ethics</span>
      </div>
    </section>
  );
};

export default CorporateGovernance;